import { type ReactNode, createContext, useContext, useId, useLayoutEffect, useRef } from "react";
import { ModalPortal } from "./ModalPortal.js";
import { containModalFocus, focusModalIfTop } from "../lib/modal-focus.js";

type Accent = "magenta" | "electric" | "gold";
const ModalDepth = createContext(0);

const ACCENT_VAR: Record<Accent, string> = {
  magenta: "var(--color-mx-magenta)",
  electric: "var(--color-mx-electric)",
  gold: "var(--color-mx-gold)",
};

/**
 * Brand-styled confirmation dialog — the single, app-wide replacement for
 * window.confirm. Mirrors DeleteDocModal's surface (mx-modal-backdrop/panel,
 * accent bar, eyebrow) so every confirm reads the same. Presentational: the
 * caller owns the action and (for async actions) passes `busy`/`error`.
 *
 * Accent colours the top bar, eyebrow, and confirm button — use `magenta` for
 * destructive/irreversible actions so they never read as the safe default,
 * `electric` for neutral confirms.
 *
 * a11y: focuses the cancel button on open (safe default), traps Escape (closes
 * unless busy), labels the dialog by its title, and renders as role="dialog".
 */
export function ConfirmModal({
  eyebrow,
  title,
  body,
  confirmLabel,
  cancelLabel = "Cancel",
  busyLabel,
  accent = "magenta",
  busy = false,
  error,
  onConfirm,
  onClose,
  testId,
}: {
  eyebrow: string;
  title: ReactNode;
  body?: ReactNode;
  confirmLabel: string;
  cancelLabel?: string;
  busyLabel?: string;
  accent?: Accent;
  busy?: boolean;
  error?: string | null;
  onConfirm: () => void;
  onClose: () => void;
  testId?: string;
}) {
  const cancelRef = useRef<HTMLButtonElement>(null);
  const depth = useContext(ModalDepth);
  const panelRef = useRef<HTMLDivElement>(null);
  const latest = useRef({ busy, onClose });
  latest.current = { busy, onClose };
  const accentColor = ACCENT_VAR[accent];

  useLayoutEffect(() => {
    if (!panelRef.current) return;
    return containModalFocus(panelRef.current, cancelRef.current, () => {
      if (!latest.current.busy) latest.current.onClose();
    }, depth);
  }, [depth]);

  useLayoutEffect(() => {
    if (busy && panelRef.current?.contains(document.activeElement)) focusModalIfTop(panelRef.current);
  }, [busy]);

  const titleId = useId();
  const bodyId = useId();

  return (
    // Portalled: `.mx-modal-backdrop` is `fixed; inset: 0`, which is only
    // viewport-relative while no ancestor establishes a containing block — and
    // this dialog is opened from the deck list's STICKY folder sidebar, whose
    // stacking context left the confirm button unclickable (a folder delete
    // hung for the full 15-minute test timeout). See ModalPortal.
    <ModalDepth.Provider value={depth + 1}>
    <ModalPortal>
      <div
        className="mx-modal-backdrop"
      onClick={
        (e) => {
              // Stop propagation so a backdrop click doesn't also close a
              // parent modal this confirm may be layered over (e.g. the share
              // dialog's "make owner" confirm).
              e.stopPropagation();
              if (!busy) onClose();
            }
      }
    >
      <div
        ref={panelRef}
        className="mx-modal-panel"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={body ? bodyId : undefined}
        tabIndex={-1}
        data-testid={testId}
      >
        <span
          aria-hidden="true"
          className="absolute top-0 left-0 h-[3px] w-[14%]"
          style={{ background: accentColor }}
        />
        <p className="mx-eyebrow mb-3" style={{ color: accentColor }}>
          {eyebrow}
        </p>
        <h2
          id={titleId}
          className="text-2xl font-light tracking-[-0.018em] mb-2"
        >
          {title}
        </h2>
        {body && (
          <div id={bodyId} className="text-sm text-white/60 mb-6 leading-relaxed">
            {body}
          </div>
        )}
        {error && (
          <p
            className="text-sm mb-4"
            style={{ color: accentColor }}
            data-testid="confirm-modal-error"
          >
            {error}
          </p>
        )}
        <div className="flex justify-end gap-2 pt-2">
          <button
            ref={cancelRef}
            type="button"
            onClick={onClose}
            disabled={busy}
            className="mx-btn-text text-sm"
          >
            {cancelLabel}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={busy}
            data-testid="confirm-modal-confirm"
            className="mx-confirm-action text-sm inline-flex items-center gap-1.5 px-3.5 py-1.5 border transition-colors duration-150 disabled:opacity-60 disabled:cursor-not-allowed"
            style={{ borderColor: accentColor, color: accentColor }}
          >
            {busy ? (busyLabel ?? `${confirmLabel}…`) : confirmLabel}
          </button>
        </div>
      </div>
    </div>
    </ModalPortal>
    </ModalDepth.Provider>
  );
}
